import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  Alert, Box, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Link, MenuItem, Paper, Select, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material'
import { PageTitle } from '../components/atoms/PageTitle'
import { PageSubtitle } from '../components/atoms/PageSubtitle'
import { Button } from '../components/atoms/Button'
import { Text } from '../components/atoms/Text'
import { callAxios } from '../hooks/useAxios'
import { useApp } from '../hooks/useApp'
import { downloadFile } from '../utils/download'
import { useNavigate } from 'react-router-dom'

interface TalenomStatus {
  baseUrl: string
  customerNumber: string | null
  missing: string[]
  accounts: boolean
}

interface Overview {
  runs: Run[]
  talenom: TalenomStatus
  payer: { name: string, businessId: string, iban: string | null, bic: string | null }
}

interface Run {
  year: number
  quarter: number
  label: string
  status: 'draft' | 'locked' | 'recorded'
  invoiceDate: string
  paymentDate: string
  emailSubject: string
  emailBody: string
  warnings: string[]
  computedAt: string
  lockedAt: string | null
  recordedAt: string | null
  job: string | null
  jobHeartbeat: string | null
  jobResult: string | null
}

interface Sums { count: number, exclCents: number, vatCents: number, totalCents: number }

interface Stats {
  fcr: {
    summary?: { count: number, raw: number, gross: number, fee: number, net: number, deducted: number, unassigned: number, no_schedule: number }
    fingrid?: { compensation: number, sanction: number, total: number }
    computed_at?: string
  }
  byStatus: Record<'payable' | 'zero' | 'skipped', Sums>
  byVat: { registered: Sums, other: Sums }
  totalCents: number
  medianCents: number
  maxCents: number
  months: { month: string, exclCents: number, grossCents: number, deductedCents: number, netCents: number }[]
  talenom: Record<string, number>
  emails: { sent: number, failed: number, noAddress: number }
  warnings: number
}

interface Statement {
  id: number
  invoiceNumber: string
  status: 'payable' | 'zero' | 'skipped'
  skipReason: string | null
  userId: number | null
  name: string | null
  email: string | null
  businessId: string | null
  vatRegistered: boolean
  iban: string | null
  powerW: number
  hwIds: string[]
  exclCents: number
  vatCents: number
  totalCents: number
  warnings: string[]
  talenomState: string | null
  talenomError: string | null
  // In Talenom, but its accounting rows did not go in: sending again redoes them.
  talenomRowsFailed: boolean
  // The statement's own dates when it was sent after the run's payment date; null: the run's.
  invoiceDate: string | null
  paymentDate: string | null
  emailedAt: string | null
  emailError: string | null
}

interface RunState { run: Run | null, stats: Stats | null, statements: Statement[] }

type Action = 'lock' | 'unlock' | 'record' | 'talenom_send' | 'talenom_approve' | 'emails'

const MAX_ROWS = 200

// Quarters the old invoicing script paid, importable from its paid files (Tuo vanha neljännes).
const LEGACY_QUARTERS: [number, number][] = [[2026, 1], [2026, 2]]

const eur = (cents: number): string =>
  (cents / 100).toLocaleString('fi-FI', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'

const eurF = (euros: number | undefined): string => euros === undefined ? '-' : eur(Math.round(euros * 100))

const isoDate = (d: Date): string => d.toISOString().slice(0, 10)

// Today in the browser's (Finnish) time zone, YYYY-MM-DD: the server dates a late statement by Helsinki time.
const localIsoDate = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

const fiDate = (iso: string): string => {
  const [y, m, d] = iso.split('-')
  return `${Number(d)}.${Number(m)}.${y}`
}

/** The quarter before today's, the one normally paid. */
function previousQuarter(): [number, number] {
  const now = new Date()
  const q = Math.floor(now.getMonth() / 3) + 1
  return q === 1 ? [now.getFullYear() - 1, 4] : [now.getFullYear(), q - 1]
}

export function AdminReservePayoutsPage(): React.ReactElement {

  const { t } = useTranslation()
  const app = useApp()
  const navigate = useNavigate()

  const [overview, setOverview] = useState<Overview | null>(null)
  const [[year, quarter], setQuarter] = useState<[number, number]>(previousQuarter())
  const [state, setState] = useState<RunState | null>(null)
  const [busy, setBusy] = useState(false)
  const [confirm, setConfirm] = useState<Action | null>(null)
  const [confirmOne, setConfirmOne] = useState<Statement | null>(null)
  const [filter, setFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'payable' | 'zero' | 'skipped' | 'warnings' | 'problems'>('all')

  const today = new Date()
  const [invoiceDate, setInvoiceDate] = useState(isoDate(today))
  const [paymentDate, setPaymentDate] = useState(isoDate(new Date(today.getTime() + 7 * 86_400_000)))
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')

  const base = `/admin/payouts/${year}/${quarter}`

  const loadOverview = () => {
    callAxios<Overview>({ url: '/admin/payouts' }).then(res => {
      if (res.success) setOverview(res.data)
      else app.addError(res)
    })
  }

  const apply = (data: RunState) => {
    setState(data)
    if (data.run) {
      setInvoiceDate(data.run.invoiceDate)
      setPaymentDate(data.run.paymentDate)
      setSubject(data.run.emailSubject)
      setBody(data.run.emailBody)
    }
  }

  const load = (quiet = false) => {
    callAxios<RunState>({ url: base }).then(res => {
      if (res.success) apply(res.data)
      else if (!quiet) app.addError(res)
    })
  }

  const call = (method: 'POST' | 'PATCH', url: string, data?: object, info?: string) => {
    setBusy(true)
    callAxios<RunState>({ method, url }, data).then(res => {
      setBusy(false)
      if (res.success) {
        apply(res.data)
        if (info) app.addInfo(info)
      } else {
        app.addError(res)
      }
    })
  }

  useEffect(() => {
    loadOverview()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setState(null)
    load()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, quarter])

  // Follow a running step.
  const jobRunning = !!state?.run?.job
  useEffect(() => {
    if (!jobRunning) return
    const timer = setInterval(() => load(true), 5000)
    return () => clearInterval(timer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobRunning, year, quarter])

  const compute = () => call('POST', `${base}/compute`,
    { invoice_date: invoiceDate, payment_date: paymentDate }, t('Draft computed.'))

  // The old script's paid files of the quarter, read here and sent as text.
  const [legacyFiles, setLegacyFiles] = useState<{ name: string, text: string }[]>([])
  const pickLegacyFiles = (list: FileList | null) =>
    Promise.all(Array.from(list || []).map(async f => ({ name: f.name, text: await f.text() }))).then(setLegacyFiles)
  const importLegacy = () => call('POST', `${base}/import`, { files: legacyFiles }, t('Quarter imported.'))

  const runAction = (action: Action) => {
    setConfirm(null)
    if (action === 'lock' || action === 'unlock' || action === 'record') {
      call('POST', `${base}/${action}`, undefined, t('Done.'))
    } else {
      call('POST', `${base}/jobs/${action}`, undefined, t('Started.'))
    }
  }

  const download = (url: string, filename: string) => {
    downloadFile(url, filename).then(ok => { if (!ok) app.addError(t('Download failed.')) })
  }

  const sendOne = (s: Statement) => {
    setConfirmOne(null)
    call('POST', `/admin/payouts/statements/${s.id}/talenom`, undefined, `${s.invoiceNumber}: ${t('sent to Talenom')}`)
  }

  const refreshPayee = (s: Statement) =>
    call('POST', `/admin/payouts/statements/${s.id}/payee`, undefined, `${s.invoiceNumber}: ${t('payee read again')}`)

  const testEmail = (s: Statement) => {
    callAxios<{ sent: string }>({ method: 'POST', url: `/admin/payouts/statements/${s.id}/test-email` }).then(res => {
      if (res.success) app.addInfo(`${t('Test email sent to')} ${res.data.sent}`)
      else app.addError(res)
    })
  }

  const run = state?.run
  const stats = state?.stats
  const statements = state?.statements || []
  const locked = run && run.status !== 'draft'
  const fcr = stats?.fcr?.summary
  // "external": paid by the old script's payment file (an imported quarter), never sent again.
  const pendingTalenom = statements.filter(s => s.status === 'payable' && !['created', 'approved', 'external'].includes(s.talenomState || '')).length
  // Sent today after its payment date, a statement is dated today and its PDF created again.
  const todayIso = localIsoDate(new Date())
  const late = (s: Statement) => !!run && (s.paymentDate || run.paymentDate) < todayIso
  const lateTalenom = statements.filter(s => s.status === 'payable' && !['created', 'approved', 'external'].includes(s.talenomState || '') && late(s)).length
  const createdTalenom = statements.filter(s => s.talenomState === 'created').length
  const pendingEmails = statements.filter(s => s.status !== 'skipped' && s.email && !s.emailedAt).length
  const talenomReady = overview && overview.talenom.missing.length === 0

  const needle = filter.trim().toLowerCase()
  const matches = statements.filter(s => {
    if (statusFilter === 'warnings' && s.warnings.length === 0) return false
    if (statusFilter === 'problems' && !(s.talenomError || s.emailError || s.talenomState === 'unknown')) return false
    if (['payable', 'zero', 'skipped'].includes(statusFilter) && s.status !== statusFilter) return false
    return !needle || [s.invoiceNumber, s.name, s.email, s.businessId, s.iban, String(s.userId), ...s.hwIds]
      .some(v => (v || '').toLowerCase().includes(needle))
  })

  const quarters: [number, number][] = (() => {
    const list: [number, number][] = (overview?.runs || []).map(r => [r.year, r.quarter])
    const now = new Date()
    for (const q of [...LEGACY_QUARTERS, previousQuarter(), [now.getFullYear(), Math.floor(now.getMonth() / 3) + 1] as [number, number]]) {
      if (!list.some(([y, n]) => y === q[0] && n === q[1])) list.push(q)
    }
    return list.sort((a, b) => (b[0] * 10 + b[1]) - (a[0] * 10 + a[1]))
  })()

  const confirmTexts: Record<Action, string> = {
    lock: t('Lock the amounts and the PDFs? The draft can no longer be recomputed (unlocking is possible until something is sent).'),
    unlock: t('Back to draft?'),
    record: t('Record the quarter as paid? The months become final on the profit pages of the customers, and the customers can download their PDFs.'),
    talenom_send: `${t('Create a Talenom purchase invoice for every payable statement not yet there?')} (${pendingTalenom})` +
      (lateTalenom > 0
        ? ` ${t('{{late}} of them are past their payment date: they are dated today ({{date}}) and their PDFs are created again.', { late: lateTalenom, date: fiDate(todayIso) })}`
        : ''),
    talenom_approve: `${t('Approve the created Talenom invoices for automatic payment on the payment date?')} (${createdTalenom})`,
    emails: `${t('Email the PDF to every customer not yet emailed?')} (${pendingEmails})`
  }

  return (
    <Box>
      <PageTitle title={t('Reserve payouts')}/>
      <Box sx={{ mb: 2 }}>
        <Trans>Quarterly reserve compensation to customers: the amounts come from FCR (the Money page), the payees from the reserve settings of the users. Every step is started here by hand; nothing happens automatically.</Trans>
      </Box>

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        {overview && <Chip
          label={`${overview.payer.name} ${overview.payer.businessId}` +
            (overview.payer.iban ? ` · ${overview.payer.iban}` : '')}/>}
        {overview && <Chip
          color={talenomReady ? 'success' : 'default'}
          label={overview.talenom.missing.length > 0
            ? `Talenom: ${t('not configured')} (${overview.talenom.missing.join(', ')})`
            : `Talenom: ${overview.talenom.baseUrl} · ${overview.talenom.customerNumber}` +
              (overview.talenom.accounts ? '' : ` · ${t('no accounts set')}`)}/>}
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Select
          size="small"
          data-cy='select-Quarter'
          value={`${year}-${quarter}`}
          onChange={e => {
            const [y, q] = String(e.target.value).split('-').map(Number)
            setQuarter([y, q])
          }}
        >
          {quarters.map(([y, q]) => <MenuItem key={`${y}-${q}`} value={`${y}-${q}`}>{`Q${q}/${y}`}</MenuItem>)}
        </Select>
        {run && <Chip label={`${t(`PayoutStatus-${run.status}`)}`} color={run.status === 'draft' ? 'default' : 'primary'}/>}
        {run?.job && <Chip color="warning" label={`${t('Running')}: ${run.job}`}/>}
      </Stack>

      {(!run || run.status === 'draft') &&
        <Box sx={{ border: '1px solid rgba(128,128,128,0.35)', borderRadius: 2, p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Text label={t('Invoice date')} type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)}/>
            <Text label={t('Payment date')} type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)}/>
          </Stack>
          <Box sx={{ mt: 1, fontSize: '0.9em', opacity: 0.8 }}>
            {t('Customers of payout partners (e.g. Rasol) are skipped:')}{' '}
            <Link component="button" data-cy='link-payout-partners' onClick={() => navigate('/admin/payout-partners')}>
              {t('Menu-Payout Partners')}
            </Link>
          </Box>
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button data-cy='button-Compute' onClick={compute} disabled={busy}>
              {run ? t('Recompute draft') : t('Compute draft')}
            </Button>
            {run && <Button data-cy='button-Lock' onClick={() => setConfirm('lock')} disabled={busy}><Trans>Lock</Trans></Button>}
          </Stack>
        </Box>}

      {!run && state && <Box sx={{ opacity: 0.6 }}><Trans>No run for this quarter yet.</Trans></Box>}

      {!run && state && LEGACY_QUARTERS.some(([y, q]) => y === year && q === quarter) &&
        <Box sx={{ border: '1px solid rgba(128,128,128,0.35)', borderRadius: 2, p: 2, my: 2 }} data-cy='legacy-import'>
          <PageSubtitle title={t('Import from the old invoicing script')}/>
          <Box sx={{ my: 1, fontSize: '0.9em', opacity: 0.8 }}>
            {t('The paid files of the quarter from scripts/reserve-profit-invoicing: the three monthly CSVs, sepa-payment.xml and the Rasol list. Customers paid by the payment file are marked paid; one left unpaid can be paid now as a late payee.')}
          </Box>
          <input type="file" multiple accept=".csv,.xml,.txt" data-cy='input-legacy-files' onChange={e => pickLegacyFiles(e.target.files)}/>
          {legacyFiles.length > 0 && <Box sx={{ mt: 1, fontSize: '0.85em' }}>{legacyFiles.map(f => f.name).join(', ')}</Box>}
          <Box sx={{ mt: 2 }}>
            <Button data-cy='button-Import legacy' onClick={importLegacy} disabled={busy || legacyFiles.length === 0}>
              {t('Import')}
            </Button>
          </Box>
        </Box>}

      {run && stats && <>
        <Box sx={{ opacity: 0.7, fontSize: '0.9em', mb: 1 }}>
          {t('Computed')} {new Date(run.computedAt).toLocaleString()}
          {run.lockedAt && ` · ${t('locked')} ${new Date(run.lockedAt).toLocaleString()}`}
          {run.recordedAt && ` · ${t('recorded as paid')} ${new Date(run.recordedAt).toLocaleString()}`}
          {` · ${t('invoice date')} ${run.invoiceDate} · ${t('payment date')} ${run.paymentDate}`}
        </Box>
        {run.jobResult && <Alert severity="info" sx={{ mb: 1 }}>{run.jobResult}</Alert>}
        {run.warnings.map((w, i) => <Alert key={i} severity="warning" sx={{ mb: 0.5 }}>{w}</Alert>)}

        <PageSubtitle sx={{ mt: 3 }} title={t('Check against FCR')}/>
        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell/>
                <TableCell align="right"><Trans>Customers</Trans></TableCell>
                <TableCell align="right"><Trans>Compensation</Trans></TableCell>
                <TableCell align="right"><Trans>Incl. VAT</Trans></TableCell>
                <TableCell align="right"><Trans>Fee took</Trans></TableCell>
                <TableCell align="right"><Trans>Paid</Trans></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>FCR (Money)</TableCell>
                <TableCell align="right">{fcr?.count ?? '-'}</TableCell>
                <TableCell align="right">{eurF(fcr?.raw)}</TableCell>
                <TableCell align="right">{eurF(fcr?.gross)}</TableCell>
                <TableCell align="right">{eurF(fcr?.deducted)}</TableCell>
                <TableCell align="right">{eurF(fcr?.net)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Trans>Statements</Trans></TableCell>
                <TableCell align="right">{statements.length}</TableCell>
                <TableCell align="right">{eur(stats.months.reduce((a, m) => a + m.exclCents, 0))}</TableCell>
                <TableCell align="right">{eur(stats.months.reduce((a, m) => a + m.grossCents, 0))}</TableCell>
                <TableCell align="right">{eur(stats.months.reduce((a, m) => a + m.deductedCents, 0))}</TableCell>
                <TableCell align="right">{eur(stats.totalCents)}</TableCell>
              </TableRow>
              {stats.months.map(m =>
                <TableRow key={m.month} sx={{ opacity: 0.75 }}>
                  <TableCell sx={{ pl: 4 }}>{m.month}</TableCell>
                  <TableCell/>
                  <TableCell align="right">{eur(m.exclCents)}</TableCell>
                  <TableCell align="right">{eur(m.grossCents)}</TableCell>
                  <TableCell align="right">{eur(m.deductedCents)}</TableCell>
                  <TableCell align="right">{eur(m.netCents)}</TableCell>
                </TableRow>)}
            </TableBody>
          </Table>
        </TableContainer>

        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell/>
                <TableCell align="right"><Trans>Count</Trans></TableCell>
                <TableCell align="right"><Trans>VAT 0 %</Trans></TableCell>
                <TableCell align="right"><Trans>VAT</Trans></TableCell>
                <TableCell align="right"><Trans>Total</Trans></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {([
                [t('Paid by us'), stats.byStatus.payable],
                [t('Fee took everything (PDF only)'), stats.byStatus.zero],
                [t('Skipped (not paid by us)'), stats.byStatus.skipped],
                [t('VAT-registered payees'), stats.byVat.registered],
                [t('Other payees'), stats.byVat.other]
              ] as [string, Sums][]).map(([label, s]) =>
                <TableRow key={label}>
                  <TableCell>{label}</TableCell>
                  <TableCell align="right">{s.count}</TableCell>
                  <TableCell align="right">{eur(s.exclCents)}</TableCell>
                  <TableCell align="right">{eur(s.vatCents)}</TableCell>
                  <TableCell align="right">{eur(s.totalCents)}</TableCell>
                </TableRow>)}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ opacity: 0.7, fontSize: '0.9em', mb: 2 }}>
          {`${t('Median payment')} ${eur(stats.medianCents)} · ${t('largest')} ${eur(stats.maxCents)}`}
          {stats.fcr.fingrid && ` · Fingrid ${eurF(stats.fcr.fingrid.total)}`}
          {fcr && fcr.no_schedule > 0 && ` · ${fcr.no_schedule} ${t('customers without a fee schedule')}`}
        </Box>

        {locked && <>
          <PageSubtitle sx={{ mt: 3 }} title={t('Payment')}/>
          <Stack direction="row" spacing={2} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
            <Button data-cy='button-Download payment file' onClick={() => download(`${base}/pain001`, `reservikorvaukset-${run.label}.xml`)}>
              <Trans>Payment file (pain.001)</Trans>
            </Button>
            <Button data-cy='button-Download CSV' onClick={() => download(`${base}/csv`, `reservikorvaukset-${run.label}.csv`)}>
              <Trans>Statement list (CSV)</Trans>
            </Button>
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }} alignItems="center">
            <Button data-cy='button-Talenom send' onClick={() => setConfirm('talenom_send')} disabled={busy || jobRunning || !talenomReady || pendingTalenom === 0}>
              {t('Send to Talenom')} ({pendingTalenom})
            </Button>
            <Button data-cy='button-Talenom approve' onClick={() => setConfirm('talenom_approve')} disabled={busy || jobRunning || !talenomReady || createdTalenom === 0}>
              {t('Approve in Talenom')} ({createdTalenom})
            </Button>
            <Box sx={{ opacity: 0.7, fontSize: '0.9em' }}>
              {Object.entries(stats.talenom).map(([k, v]) => `${k === 'none' ? t('not sent') : k} ${v}`).join(' · ')}
            </Box>
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }} alignItems="center">
            <Button data-cy='button-Send emails' onClick={() => setConfirm('emails')} disabled={busy || jobRunning || pendingEmails === 0}>
              {t('Send emails')} ({pendingEmails})
            </Button>
            <Box sx={{ opacity: 0.7, fontSize: '0.9em' }}>
              {`${t('sent')} ${stats.emails.sent} · ${t('failed')} ${stats.emails.failed} · ${t('no address')} ${stats.emails.noAddress}`}
            </Box>
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            {run.status !== 'draft' &&
              <Button data-cy='button-Record paid' onClick={() => setConfirm('record')} disabled={busy || jobRunning}><Trans>Record as paid</Trans></Button>}
            {run.status === 'locked' &&
              <Button data-cy='button-Unlock' onClick={() => setConfirm('unlock')} disabled={busy || jobRunning}><Trans>Unlock</Trans></Button>}
          </Stack>
        </>}

        <PageSubtitle sx={{ mt: 3 }} title={t('Customer email')}/>
        <Box sx={{ opacity: 0.7, fontSize: '0.9em', mb: 1 }}>
          {'{quarter} {amount} {payment_date} {invoice_number}'}
        </Box>
        <Text label={t('Subject')} value={subject} onChange={e => setSubject(e.target.value)} fullWidth sx={{ mb: 1 }}/>
        <Text label={t('Message')} value={body} onChange={e => setBody(e.target.value)} fullWidth multiline minRows={6}/>
        <Button sx={{ mt: 1, mb: 2 }} data-cy='button-Save email text' disabled={busy}
          onClick={() => call('PATCH', `${base}/email`, { email_subject: subject, email_body: body }, t('Saved.'))}>
          <Trans>Save</Trans>
        </Button>

        <PageSubtitle sx={{ mt: 3 }} title={`${t('Customers')} (${matches.length})`}/>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
          <Text
            label={t('Search by name, email, invoice number, IBAN or device')}
            value={filter}
            onChange={e => setFilter(e.target.value)}
            sx={{ minWidth: '22rem' }}
          />
          <Select size="small" value={statusFilter} onChange={e => setStatusFilter(e.target.value as typeof statusFilter)}>
            <MenuItem value="all">{t('All')}</MenuItem>
            <MenuItem value="payable">{t('PayoutStatement-payable')}</MenuItem>
            <MenuItem value="zero">{t('PayoutStatement-zero')}</MenuItem>
            <MenuItem value="skipped">{t('PayoutStatement-skipped')}</MenuItem>
            <MenuItem value="warnings">{`${t('With warnings')} (${stats.warnings})`}</MenuItem>
            <MenuItem value="problems">{t('Talenom or email problems')}</MenuItem>
          </Select>
        </Stack>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><Trans>Invoice</Trans></TableCell>
                <TableCell><Trans>Payee</Trans></TableCell>
                <TableCell align="right"><Trans>Total</Trans></TableCell>
                <TableCell><Trans>State</Trans></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {matches.slice(0, MAX_ROWS).map(s =>
                <TableRow key={s.id} data-cy={`statement-${s.invoiceNumber}`}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ fontFamily: 'monospace' }}>{s.invoiceNumber}</Box>
                    <Stack direction="row" spacing={1.5} sx={{ mt: 0.5, fontSize: '0.85em' }}>
                      {s.status !== 'skipped' && <>
                        <Link component="button" data-cy={`pdf-${s.invoiceNumber}`} onClick={() => download(`/admin/payouts/statements/${s.id}/pdf`, `${s.invoiceNumber}.pdf`)}>PDF</Link>
                        <Link component="button" data-cy={`test-email-${s.invoiceNumber}`} onClick={() => testEmail(s)}>{t('Test email')}</Link>
                      </>}
                      {locked && talenomReady && !jobRunning && !busy && s.status === 'payable' && ([null, 'failed', 'unknown'].includes(s.talenomState) || s.talenomRowsFailed) &&
                        <Link component="button" data-cy={`talenom-${s.invoiceNumber}`} onClick={() => setConfirmOne(s)}>{t('Send to Talenom')}</Link>}
                      {/* Any statement Talenom does not have yet: a fixed IBAN or an added business id reaches its invoice. */}
                      {locked && !busy && !!s.userId && !['created', 'approved', 'unknown', 'external'].includes(s.talenomState || '') &&
                        <Link component="button" data-cy={`payee-${s.invoiceNumber}`} onClick={() => refreshPayee(s)}>{t('Update payee')}</Link>}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Box>{s.name || '-'}{s.businessId && <Box component="span" sx={{ opacity: 0.6 }}>{` · ${s.businessId}`}</Box>}</Box>
                    <Box sx={{ opacity: 0.6, fontSize: '0.85em' }}>{[s.email, s.iban, s.hwIds.join(' ')].filter(Boolean).join(' · ')}</Box>
                    {s.warnings.map((w, i) => <Box key={i} sx={{ color: 'warning.main', fontSize: '0.85em' }}>{w}</Box>)}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {eur(s.totalCents)}
                    {s.vatRegistered && <Box sx={{ opacity: 0.6, fontSize: '0.8em' }}>{`${t('VAT')} ${eur(s.vatCents)}`}</Box>}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.85em' }}>
                    <Box>{t(`PayoutStatement-${s.status}`)}{s.skipReason && `: ${s.skipReason}`}</Box>
                    {s.talenomState && <Box>{s.talenomState === 'external' ? t('Paid with the old payment file') : `Talenom: ${s.talenomState}`}</Box>}
                    {s.paymentDate && <Box data-cy={`own-date-${s.invoiceNumber}`}>{`${t('payment date')} ${fiDate(s.paymentDate)}`}</Box>}
                    {s.talenomError && <Box sx={{ color: 'error.main' }}>{s.talenomError}</Box>}
                    {s.emailedAt && <Box>{`${t('emailed')} ${new Date(s.emailedAt).toLocaleDateString()}`}</Box>}
                    {s.emailError && <Box sx={{ color: 'error.main' }}>{s.emailError}</Box>}
                  </TableCell>
                </TableRow>)}
            </TableBody>
          </Table>
        </TableContainer>
        {matches.length > MAX_ROWS &&
          <Box sx={{ opacity: 0.6, mt: 1 }}>{`${matches.length - MAX_ROWS} ${t('more - narrow the search')}`}</Box>}
      </>}

      <Dialog open={!!confirm || !!confirmOne} onClose={() => { setConfirm(null); setConfirmOne(null) }}>
        <DialogTitle><Trans>Are you sure?</Trans></DialogTitle>
        <DialogContent>
          {confirm && confirmTexts[confirm]}
          {!confirm && confirmOne &&
            `${t('Create a Talenom purchase invoice for this statement?')} ${confirmOne.invoiceNumber} · ${confirmOne.name || '-'} · ${eur(confirmOne.totalCents)}`}
          {!confirm && confirmOne && late(confirmOne) &&
            <Box sx={{ mt: 2, fontWeight: 'bold' }} data-cy='confirm-redate'>
              {t('The payment date has passed: the PDF is created again dated today ({{date}}), as invoice and payment date.', { date: fiDate(todayIso) })}
            </Box>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirm(null); setConfirmOne(null) }}><Trans>Cancel</Trans></Button>
          <Button data-cy='button-Confirm'
            onClick={() => confirm ? runAction(confirm) : confirmOne && sendOne(confirmOne)}><Trans>Confirm</Trans></Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
